import React from 'react';
import { Play, Square, RotateCcw } from 'lucide-react';
import { HOLES, DISTANCES, TOTAL_PAR, totalFor } from './courseData';
import { useCountUp } from './useReducedMotion';
import TeeSelector from './TeeSelector';
import ViewToggle from './ViewToggle';
import HoleNav from './HoleNav';

const Sep = () => <span className="text-[#c9a962]/60 mx-2" aria-hidden="true">•</span>;

const HolePanel = ({ hole, tee, view, playing, onSelectHole, onTee, onView, onTour, onStop, onReset }) => {
  const h = hole ? HOLES[hole - 1] : null;
  const shown = useCountUp(h ? DISTANCES[tee][hole - 1] : totalFor(tee));

  return (
    <div
      className="h-full flex flex-col gap-4 bg-[#0f2419]/70 backdrop-blur-md border border-[#c9a962]/20 rounded-2xl p-5"
      data-testid="hole-panel"
    >
      <div key={hole ?? 'course'} style={{ animation: 'fadeSlideIn .5s ease-out' }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-6 h-px bg-[#c9a962]" />
          <span className="font-montserrat text-[#c9a962] text-[10px] tracking-[0.3em] uppercase" data-testid="hole-panel-kicker">
            {h ? 'Trou sélectionné' : 'Le parcours'}
          </span>
        </div>
        <h3 className="font-playfair text-white text-3xl lg:text-4xl leading-none mb-2.5" data-testid="hole-panel-title">
          {h ? `Trou ${h.number}` : 'Vue d\u2019ensemble'}
        </h3>
        <p className="font-montserrat text-white/75 text-sm flex items-baseline flex-wrap" data-testid="hole-panel-summary">
          {h ? (
            <>
              <span className="font-playfair text-[#c9a962] text-xl tabular-nums" data-testid="hole-panel-distance">{shown.toLocaleString('fr-FR')}</span>
              <span className="text-[#c9a962] ml-1">m</span>
              <Sep /><span data-testid="hole-panel-par">Par {h.par}</span>
              <Sep /><span data-testid="hole-panel-hcp">HCP {h.hcp}</span>
            </>
          ) : (
            <>
              <span>{HOLES.length} trous</span>
              <Sep /><span data-testid="hole-panel-par">Par {TOTAL_PAR}</span>
              <Sep />
              <span className="font-playfair text-[#c9a962] text-xl tabular-nums" data-testid="hole-panel-distance">{shown.toLocaleString('fr-FR')}</span>
              <span className="text-[#c9a962] ml-1">m</span>
            </>
          )}
        </p>
      </div>

      <div className="border-t border-[#c9a962]/15 pt-4">
        <TeeSelector tee={tee} hole={hole} onChange={onTee} />
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-2">
        <HoleNav hole={hole} onSelect={onSelectHole} />
        <ViewToggle view={view} onChange={onView} />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={playing ? onStop : onTour}
            data-testid="tour-button"
            className="btn-gold flex-1 flex items-center justify-center gap-2 rounded-full px-4 min-h-[40px] text-[11px] tracking-[0.12em] uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {playing ? <Square className="w-3 h-3 fill-current" /> : <Play className="w-3 h-3 fill-current" />}
            {playing ? 'Arrêter' : 'Parcours complet'}
          </button>
          <button
            type="button"
            onClick={onReset}
            data-testid="reset-button"
            aria-label="Réinitialiser la carte"
            className="w-10 h-10 flex-none flex items-center justify-center rounded-full border border-[#c9a962]/25 text-white/60 hover:text-[#c9a962] hover:border-[#c9a962]/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a962]"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HolePanel;
